import { Flex, GapSize } from "@/components/atoms/flex";
import { Button, Switch, Text, mergeClasses } from "@fluentui/react-components";
import { CircleFilled, CircleRegular } from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ContactSearch } from "@/components/molecules/contactSearch";
import { useStyle } from "./styles";
import {
	AgentInteractions,
	ContactDto,
	ContactListItem,
	InteractionDto,
	ShortContactInfo,
} from "@/repository/models";
import { ContactForm } from "../contactForm";
import { AddOrUpdateContactProps } from "./types";
import { useMessage } from "@/common/messages";
import {
	ContactsKey,
	FavoritesKey,
	addCustomerJourneyContact,
	updateCustomerJourneyContact,
} from "@/repository/queryFns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentInteractionStore } from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types";
import { captureError } from "@/common/helpers/sentryHelper";
import { customerHistoryQueryKey } from "@/repository/queryFns/customerHistory";

export const AddOrUpdateContact = ({
	identifiedContact,
	interactionType,
	interactionId,
	onContactSaved,
	onCloseDialog,
}: AddOrUpdateContactProps) => {
	const intl = useIntl();
	const classes = useStyle();

	const interactionsQueryKey = ["interactions"];
	const voicemailsQueryKey = ["voicemails"];
	const queryClient = useQueryClient();

	const { show: showMessage } = useMessage();

	const currentInteractionState = useCurrentInteractionStore();

	const [enableQueueSelection, setEnableQueueSelection] = useState(false);
	const [selectedContact, setSelectedContact] = useState<ContactListItem>();
	const [highlightedFields, setHighlightedFields] = useState<string[]>();
	const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);

	useEffect(() => {
		if (selectedContact?.queueIds?.length) {
			setEnableQueueSelection(true);
		}
	}, [selectedContact]);

	useEffect(() => {
		if (identifiedContact?.id) {
			setSelectedContact(identifiedContact);
		}
	}, [identifiedContact]);

	const onAddNewContactClick = () => {
		onContactSelected({} as ContactListItem);
	};

	const setContactPhone = useCallback(
		(contact: ContactListItem, newPhone: string): string => {
			if (!contact.mobilePhone) {
				contact.mobilePhone = newPhone;
				return "mobilePhone";
			} else if (contact.mobilePhone && !contact.businessPhone) {
				contact.businessPhone = newPhone;
				return "businessPhone";
			} else {
				contact.businessPhone = newPhone;
				return "businessPhone";
			}
		},
		[]
	);

	const invalidateQueries = useCallback(
		(key: string) => queryClient.invalidateQueries({ queryKey: [key] }),
		[queryClient]
	);

	const onContactSelected = useCallback(
		(shortContactInfo: ShortContactInfo) => {
			const contact = shortContactInfo as ContactListItem;
			const contactHighlightedFields = [];

			if (identifiedContact.mobilePhone) {
				contactHighlightedFields.push(
					setContactPhone(contact, identifiedContact.mobilePhone)
				);
			}

			if (identifiedContact.name) {
				contact.name = identifiedContact.name;
				contactHighlightedFields.push("name");
			}

			if (identifiedContact.email) {
				contact.email = identifiedContact.email;
				contactHighlightedFields.push("email");
			}

			if (identifiedContact.userPrincipalName) {
				contact.userPrincipalName = identifiedContact.userPrincipalName;
				contactHighlightedFields.push("userPrincipalName");
			}

			setSelectedContact(contact);
			setHighlightedFields(contactHighlightedFields);
		},
		[identifiedContact, setHighlightedFields]
	);

	const onAddToContactsChange = useCallback(
		(ev: React.ChangeEvent<HTMLInputElement>) => {
			setEnableQueueSelection(ev.currentTarget.checked);
		},
		[setEnableQueueSelection]
	);

	const contactInformation = useCallback(
		(contact: ContactDto, intType: InteractionType, currentContactInformation: string) => {
			switch (intType) {
				case InteractionType.WhatsApp:
				case InteractionType.Call:
					return contact.mobilePhoneNumber;
				case InteractionType.Voicemail:
					return currentContactInformation;
				case InteractionType.Email:
				case InteractionType.Webchat:
					return contact.email;
			}
			return "";
		},
		[]
	);

	const updateContactInformation = (contact: ContactDto, ai: InteractionDto) => {
		if (ai.id === interactionId) {
			ai = {
				...ai,
				name: contact.name,
				contactId: contact.id,
				contactInformation: contactInformation(contact, ai.type, ai.contactInformation),
			};
		} else if (ai.contactId == contact.id) {
			ai = {
				...ai,
				name: contact.name,
				contactInformation: contactInformation(contact, ai.type, ai.contactInformation),
			};
		}

		return ai;
	}

	const updateAgentInteractions = (contact: ContactDto) => {
		queryClient.setQueryData<AgentInteractions>(
			interactionsQueryKey,
			(currentInteractions?: AgentInteractions) => {

				const updatedActiveInteractions = currentInteractions?.active?.map(
					(ai) => {
						return updateContactInformation(contact, ai);
					}
				);
				const updatedWaitingInteractions = currentInteractions?.waiting?.map(
					(ai) => {
						return updateContactInformation(contact, ai);
					}
				);

				return {
					...currentInteractions,
					active: updatedActiveInteractions,
					waiting: updatedWaitingInteractions,
				};
			}
		);

		queryClient.setQueryData<InteractionDto[]>(voicemailsQueryKey, (oldData) => {
			var data = oldData?.map((voicemailInteraction) => {
				return updateContactInformation(contact, voicemailInteraction);
			});

			return data;
		});
		
	};

	const updateInteractionOnSaved = useCallback(
		(contact: ContactDto) => {
			updateAgentInteractions(contact);
			onContactSaved?.(contact);

			const activeInteraction = currentInteractionState.active;
			if (!activeInteraction) {
				return;
			}

			currentInteractionState.showActive({
				...activeInteraction,
				name: contact.name,
			}, true);
		},
		[interactionType, currentInteractionState, interactionId, onContactSaved]
	);

	const updateContactMutation = useMutation({
		mutationFn: updateCustomerJourneyContact,
		onSuccess: (updatedContact: ContactDto) => {
			onCloseDialog?.();
			setSelectedContact(undefined);
			setIsSaveInProgress(false);

			showMessage("global", {
				intent: "success",
				text: intl.formatMessage({
					id: "Contacts.SuccessUpdate",
				}),
			});

			setTimeout(() => {
				invalidateQueries(ContactsKey);
				invalidateQueries(FavoritesKey);
				invalidateQueries(customerHistoryQueryKey);
			}, 1000); //elastic save is actually triggering an indexing operation, which will not be immediately reflected if retrieving the contact information again

			updateInteractionOnSaved(updatedContact);
		},
		onError: (err, data) => {
			captureError(err, "Error updating contact", undefined, data);
			setIsSaveInProgress(false);
			showMessage("dialog", {
				intent: "error",
				text: intl.formatMessage({
					id: "Contacts.ErrorOnSave",
				}),
			});
		},
	});

	const addContactMutation = useMutation({
		mutationFn: addCustomerJourneyContact,
		onSuccess: (createdContact: ContactDto) => {
			onCloseDialog?.();
			setIsSaveInProgress(false);
			showMessage("global", {
				intent: "success",
				text: intl.formatMessage({
					id: "Contacts.SuccessAdd",
				}),
			});
			setTimeout(() => {
				invalidateQueries(ContactsKey);
			}, 1000);

			updateInteractionOnSaved(createdContact);
		},
		onError: (err, data) => {
			captureError(err, "Error creating contact", undefined, data);
			setIsSaveInProgress(false);
			showMessage("dialog", {
				intent: "error",
				text: intl.formatMessage({
					id: "Contacts.ErrorOnSave",
				}),
			});
		},
	});

	const onSaveContact = useCallback(
		(contact: ContactDto) => {
			contact.queueIds = contact.queueIds ?? [];

			setIsSaveInProgress(true);

			if (contact.id) {
				updateContactMutation.mutate({
					contact,
					interactionId,
					interactionType,
				});
			} else {
				addContactMutation.mutate({ contact, interactionId, interactionType });
			}
		},
		[interactionId, interactionType]
	);

	const onFormClose = () => {
		onCloseDialog?.();
		setEnableQueueSelection(false);
		setSelectedContact(undefined);
	};

	const getSaveLabel = useCallback(() => {
		if (identifiedContact.id) {
			return undefined;
		}

		return enableQueueSelection
			? intl.formatMessage({ id: "Contacts.AddToContacts" })
			: intl.formatMessage({ id: "Contacts.SaveCustomer" });
	}, [identifiedContact, enableQueueSelection]);

	return selectedContact ? (
		<Flex column>
			{!identifiedContact.id && (
				<ContactSearch
					classesNames={{
						searchInput: mergeClasses(
							classes.contactSearch,
							classes.contactSearchOnAddContact
						),
					}}
					onItemSelected={onContactSelected}
				/>
			)}
			{(!identifiedContact.id ||
				(identifiedContact.id && !identifiedContact.queueIds?.length)) && (
					<>
						<Text>
							{intl.formatMessage({
								id: "Contacts.DataSavedAsCustomerInfoLabel",
							})}
						</Text>
						<Text>
							{intl.formatMessage({
								id: "Contacts.ShouldCustomerBeAddedToContactsListLabel",
							})}
						</Text>
						<Flex vAlign="center" className={classes.addContactSwitchContainer}>
							<Text className={classes.addContactSwitchLabel}>
								{intl.formatMessage({
									id: "Contacts.AddContactUnCheckedLabel",
								})}
							</Text>
							<Switch
								className={classes.addContactSwitch}
								checked={enableQueueSelection}
								onChange={onAddToContactsChange}
								disabled={selectedContact?.queueIds?.length > 0}
								indicator={
									enableQueueSelection ? <CircleFilled /> : <CircleRegular />
								}
								label=""
							/>
							<Text className={classes.addContactSwitchLabel}>
								{intl.formatMessage({
									id: "Contacts.AddContactCheckedLabel",
								})}
							</Text>
						</Flex>
					</>
				)}
			<ContactForm
				contact={selectedContact || ({} as ContactListItem)}
				queueSelectionEnabled={enableQueueSelection}
				saveContact={onSaveContact}
				closeDialog={onFormClose}
				highlightedFields={highlightedFields}
				saveLabel={getSaveLabel()}
				isSaveInProgress={isSaveInProgress}
			/>
		</Flex>
	) : (
		<Flex column>
			<Text>
				{intl.formatMessage({
					id: "Contacts.AddOrUpdateInstructions",
				})}
			</Text>
			<Flex gap={GapSize.Large} className={classes.searchOrAddContainer}>
				<ContactSearch
					classesNames={{ searchInput: classes.contactSearch }}
					onItemSelected={onContactSelected}
				/>
				<Button appearance="primary" onClick={onAddNewContactClick}>
					{intl.formatMessage({ id: "Contacts.OrAddANewContact" })}
				</Button>
			</Flex>
		</Flex>
	);
};
